import { Image as AntdImage, Button, Modal } from 'antd'
import {
  UploadWideStyled,
  deleteButtonStyles,
  emptyIconsRowStyles,
  emptyOuterStyles,
  fileContainerStyles,
  fileMetaStyles,
  fileNameStyles,
  filePreviewThumbStyles,
  filePreviewThumbImageStyles,
  fileRowStyles,
  headerTextStyles,
  uploadButtonStyles,
  uploadButtonWideStyles
} from './UploadMediaWide.styles'
import { useEffect, useMemo, useState } from 'react'

import ArrowUp from 'assets/ArrowUp.png'
import { Box } from '@qonsoll/react-design'
import { DeleteOutlined } from '@ant-design/icons'
import ImagePlaceholder from 'assets/Image.png'
import MaskIcon from 'components/forms/MaskIcon'
import PropTypes from 'prop-types'
import { useTranslations } from '@qonsoll/translation'
import { useUploadMedia } from 'hooks'

const UploadMediaWide = (props) => {
  const {
    uploadLoading,
    accept = '*',
    maxCount,
    value,
    onChange,
    storagePath,
    setUploadLoading,
    tipId,
    setStep,
    isGuestMode,
    disabled,
    ...rest
  } = props

  const { t } = useTranslations()

  const {
    handleChange,
    files,
    handleRemoveFile,
    beforeUpload,
    handleUpload,
    customRequest
  } = useUploadMedia({
    maxCount,
    value,
    onChange,
    storagePath,
    setUploadLoading,
    uploadLoading,
    tipId,
    setStep,
    isGuestMode
  })

  const fileList = files?.files || value?.files || []

  const listFingerprint = useMemo(
    () =>
      JSON.stringify(
        (Array.isArray(fileList) ? fileList : [])
          .filter((f) => f?.uid)
          .slice(0, maxCount || undefined)
          .map((f) => ({
            u: f.uid,
            url: f.url,
            th: f.thumbUrl,
            lm: f.originFileObj?.lastModified,
            t: f.type
          }))
      ),
    [fileList, maxCount]
  )

  const objectPreviewByUid = useMemo(() => {
    const displayFiles = (Array.isArray(fileList) ? fileList : [])
      .filter((f) => f?.uid)
      .slice(0, maxCount || undefined)
    const map = {}
    displayFiles.forEach((f) => {
      if (f?.url || f?.thumbUrl || !f?.originFileObj) return
      const isMedia = f?.type?.includes('image') || f?.type?.includes('video')
      if (isMedia) {
        map[f.uid] = URL.createObjectURL(f.originFileObj)
      }
    })
    return map
  }, [listFingerprint])

  useEffect(() => {
    return () => {
      Object.values(objectPreviewByUid).forEach((u) => URL.revokeObjectURL(u))
    }
  }, [objectPreviewByUid])

  const displayFiles = (Array.isArray(fileList) ? fileList : [])
    .filter((f) => f?.uid)
    .slice(0, maxCount || undefined)

  const [videoPreview, setVideoPreview] = useState({ open: false, src: '' })

  return (
    <Box overflowY="auto" whiteSpace="nowrap" width="100%">
      <UploadWideStyled
        {...rest}
        disabled={disabled || uploadLoading}
        multiple={!maxCount || maxCount > 1}
        accept={accept}
        beforeUpload={beforeUpload}
        listType="picture"
        showUploadList={false}
        maxCount={maxCount}
        fileList={fileList}
        onChange={handleChange}
        customRequest={customRequest}
        onRemove={handleRemoveFile}>
        <Box
          style={uploadButtonWideStyles}
          aria-disabled={disabled || uploadLoading}>
          <Box style={headerTextStyles}>{t('Attach picture or video')}</Box>
          {displayFiles?.length ? (
            <Box
              style={fileContainerStyles}
              onClick={(e) => {
                e.stopPropagation()
              }}>
              {displayFiles.map((f) => {
                const isImage = f?.type?.includes('image')
                const isVideo = f?.type?.includes('video')
                const fileName =
                  f?.name || f?.originFileObj?.name || 'Unnamed file'
                const previewSrc =
                  f.url || f.thumbUrl || objectPreviewByUid[f.uid]

                return (
                  <Box key={f?.uid} style={{ ...fileRowStyles, width: '100%' }}>
                    <Box style={fileMetaStyles}>
                      {isImage && previewSrc ? (
                        <AntdImage
                          alt=""
                          src={previewSrc}
                          width={filePreviewThumbStyles.width}
                          height={filePreviewThumbStyles.height}
                          style={filePreviewThumbStyles}
                          imgStyle={filePreviewThumbImageStyles}
                          preview={{ maskClassName: 'image-preview-br' }}
                        />
                      ) : isVideo && previewSrc ? (
                        <Box
                          onClick={() =>
                            setVideoPreview({ open: true, src: previewSrc })
                          }
                          style={{
                            ...filePreviewThumbStyles,
                            cursor: 'pointer',
                            overflow: 'hidden'
                          }}>
                          <video
                            muted
                            playsInline
                            src={previewSrc}
                            style={{
                              ...filePreviewThumbImageStyles
                            }}
                          />
                        </Box>
                      ) : (
                        <MaskIcon
                          src={isImage ? ImagePlaceholder : ArrowUp}
                          height={28}
                          width={28}
                          color={isImage ? '#7A8A99' : '#5B8FD9'}
                        />
                      )}
                      <Box style={fileNameStyles}>{fileName}</Box>
                    </Box>

                    <Button
                      size="small"
                      type="dashed"
                      style={deleteButtonStyles}
                      icon={<DeleteOutlined />}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleRemoveFile(f)
                      }}
                    />
                  </Box>
                )
              })}
            </Box>
          ) : (
            <Box style={emptyOuterStyles}>
              <Box style={emptyIconsRowStyles}>
                <MaskIcon
                  src={ImagePlaceholder}
                  height={72}
                  color="#7A8A99"
                  width={76}
                />
                <MaskIcon
                  src={ArrowUp}
                  height={72}
                  color="#5B8FD9"
                  width={80}
                />
              </Box>
            </Box>
          )}
        </Box>
      </UploadWideStyled>

      <Button
        style={uploadButtonStyles}
        id="uploadMedia"
        onClick={handleUpload}
      />

      <Modal
        visible={videoPreview.open}
        footer={null}
        width={720}
        centered
        destroyOnClose
        onCancel={() => setVideoPreview({ open: false, src: '' })}>
        {videoPreview.src ? (
          <video
            controls
            playsInline
            src={videoPreview.src}
            style={{ width: '100%', maxHeight: '80vh' }}
          />
        ) : null}
      </Modal>
    </Box>
  )
}

UploadMediaWide.propTypes = {
  text: PropTypes.string,
  accept: PropTypes.string,
  maxCount: PropTypes.number,
  value: PropTypes.object,
  onChange: PropTypes.func,
  storagePath: PropTypes.string,
  setUploadLoading: PropTypes.func,
  tipId: PropTypes.string,
  setStep: PropTypes.func,
  isGuestMode: PropTypes.bool,
  disabled: PropTypes.bool,
  uploadLoading: PropTypes.bool
}

export default UploadMediaWide
